import * as XLSX from 'xlsx';

export const readExcelFile = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
 
    reader.onload = e => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
       
        const allData: any[] = [];
       
        for (const sheetName of workbook.SheetNames) {
          // Skip sheets named "Unbalanced invoices"
          if (sheetName.toLowerCase() === "unbalanced invoices") {
            console.log(`Skipping sheet: ${sheetName} as it matches "Unbalanced invoices"`);
            continue;
          }
         
          const worksheet = workbook.Sheets[sheetName];
          const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:Z100');
         
          let headerRowIndex = -1;
          const headerPatterns = [
            /invoice/i, /date/i, /ledger/i, /amount/i,
            /invoice/i, /type/i, /item/i, /quantity/i, /unit/i, /cost/i
          ];
         
          for (let r = range.s.r; r <= Math.min(range.e.r, 15); r++) {
            let matchCount = 0;
           
            for (let c = range.s.c; c <= range.e.c; c++) {
              const cellAddress = XLSX.utils.encode_cell({r, c});
              const cell = worksheet[cellAddress];
             
              if (cell && cell.v) {
                const value = String(cell.v).toLowerCase();
               
                for (const pattern of headerPatterns) {
                  if (pattern.test(value)) {
                    matchCount++;
                    break;
                  }
                }
              }
            }
           
            if (matchCount >= 3) {
              headerRowIndex = r;
              break;
            }
          }
         
          const finalHeaderRow = headerRowIndex >= 0 ? headerRowIndex : range.s.r;
         
          // Get cell styles for filtering red cells
          const sheetData = XLSX.utils.sheet_to_json(worksheet, {
            range: finalHeaderRow,
            defval: '',
            blankrows: false,
            header: 1 // Get array of arrays first to process row by row
          }) as any[][];
         
          if (sheetData && sheetData.length > 0) {
            // Extract headers from the first row
            const headers = sheetData[0];
           
            // Process each row starting from the second row
            for (let i = 1; i < sheetData.length; i++) {
              const row = sheetData[i];
              let hasRedCell = false;
             
              // Check each cell in the row for red fill color
              for (let j = 0; j < row.length; j++) {
                const cellAddress = XLSX.utils.encode_cell({r: i + finalHeaderRow, c: j});
                const cell = worksheet[cellAddress];
               
                // Check for red color in cell
                if (cell && cell.s && cell.s.fill && cell.s.fill.fgColor) {
                  const color = cell.s.fill.fgColor.rgb || cell.s.fill.fgColor.theme;
                  if (color) {
                    const colorStr = String(color).toLowerCase();
                    // Check if color contains red (FF0000) or is a red theme
                    if (colorStr.includes('ff0000') || colorStr === 'red' ||
                        (cell.s.fill.fgColor.theme === 2) || // Red theme color
                        (colorStr.startsWith('ff') && !colorStr.substring(2).match(/[1-9a-f]/i))) { // Only red component
                      hasRedCell = true;
                      break;
                    }
                  }
                }
              }
             
              // Skip rows with red cells
              if (hasRedCell) {
                console.log(`Skipping row ${i + finalHeaderRow + 1} due to red cell(s)`);
                continue;
              }
             
              // Convert row to object using headers
              const rowObject: Record<string, any> = {};
              headers.forEach((header: string, index: number) => {
                if (header) {
                  rowObject[header] = row[index] || '';
                }
              });
             
              // Add non-red row to dataset
              if (Object.keys(rowObject).length > 0) {
                allData.push(rowObject);
              }
            }
          }
        }
       
        if (allData.length === 0) {
          // Fallback processing if no valid data found with primary method
          for (const sheetName of workbook.SheetNames) {
            // Skip sheets named "Unbalanced invoices"
            if (sheetName.toLowerCase() === "unbalanced invoices") {
              continue;
            }
           
            const worksheet = workbook.Sheets[sheetName];
            const rawData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              defval: '',
              blankrows: false
            }) as any[][];
           
            if (rawData && rawData.length > 1) {
              let headerRow = 0;
              for (let i = 0; i < Math.min(rawData.length, 10); i++) {
                if (rawData[i].some(cell => cell)) {
                  headerRow = i;
                  break;
                }
              }
             
              const headers = rawData[headerRow].map((h: any) =>
                h || `Column${Math.random().toString(36).substring(2, 7)}`);
             
              // Process each row after headers, checking for red cells
              for (let i = headerRow + 1; i < rawData.length; i++) {
                const row = rawData[i];
                let hasRedCell = false;
               
                // Check each cell in the row for red fill color
                for (let j = 0; j < row.length; j++) {
                  const cellAddress = XLSX.utils.encode_cell({r: i, c: j});
                  const cell = worksheet[cellAddress];
                 
                  // Check for red color in cell
                  if (cell && cell.s && cell.s.fill && cell.s.fill.fgColor) {
                    const color = cell.s.fill.fgColor.rgb || cell.s.fill.fgColor.theme;
                    if (color) {
                      const colorStr = String(color).toLowerCase();
                      // Check if color contains red (FF0000) or is a red theme
                      if (colorStr.includes('ff0000') || colorStr === 'red' ||
                          (cell.s.fill.fgColor.theme === 2) || // Red theme color
                          (colorStr.startsWith('ff') && !colorStr.substring(2).match(/[1-9a-f]/i))) { // Only red component
                        hasRedCell = true;
                        break;
                      }
                    }
                  }
                }
               
                // Skip rows with red cells
                if (hasRedCell) {
                  continue;
                }
               
                // Convert non-red row to object
                const obj: Record<string, any> = {};
                headers.forEach((header: string, idx: number) => {
                  obj[header] = row[idx] || '';
                });
               
                if (Object.keys(obj).length > 0) {
                  allData.push(obj);
                }
              }
            }
          }
        }
       
        if (allData.length === 0) {
          reject(new Error('No valid data found in Excel file or all data was in red cells or in "Unbalanced invoices" sheet'));
        } else {
          resolve(allData);
        }
      } catch (error) {
        reject(error);
      }
    };
 
    reader.onerror = error => {
      reject(error);
    };
 
    reader.readAsArrayBuffer(file);
  });
};